// Nome do arquivo: AgenExterno.tsx

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Box
} from '@material-ui/core';
import { Link } from 'react-router-dom';
import { HighlightOff, SettingsBackupRestore } from '@material-ui/icons';
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert';
import { Pagination } from '@material-ui/lab';
import { api } from '@services/api';
import { maskCPF, verifyNumberOrString } from '@utils/masks';
import { Button } from '@components/Button';
import { MagnifyingGlass, X } from '@phosphor-icons/react';
import { ContainerButtonsTop, DivSearch } from './styles'; // A importação agora funciona!

// Interfaces
interface IAgenda {
  id_agendamento: number;
  nome: string;
  cpf_colaborador: string;
  tipo_agendamento: string;
  data_agendamento: string;
  horario: string;
  status: string;
  nome_comercial: string;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  }
);

const PER_PAGE = 30;

export const AgenExterno: React.FC = () => {
  // Estados
  const [data, setData] = useState<IAgenda[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [dialogDelete, setDialogDelete] = useState(false);
  const [edit, setEdit] = useState<IAgenda | null>(null);
  const [snack, setSnack] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<
    'success' | 'error' | 'info' | 'warning'
  >('success');
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  // Função de busca de dados
  const fetchData = async (
    page: number,
    search: string = '',
    date: string = ''
  ) => {
    try {
      setLoading(true);
      const params = {
        page,
        per_page: PER_PAGE,
        ...(search && { search }),
        ...(date && { date })
      };

      const response = await api.get(`ravim/agendamentos-wp`, { params });

      setData(response.data.results || []);
      setTotalPages(response.data.total_pages || 1);
      setTotalRecords(response.data.total_records || 0);
    } catch (error) {
      console.error('Erro ao carregar agendamentos externos:', error);
      setSnack(true);
      setMessage('Erro ao carregar dados. Tente novamente.');
      setSeverity('error');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // Efeito para buscar dados com debounce
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    const timeout = setTimeout(() => {
      fetchData(currentPage, searchValue, filterDate);
    }, 500);
    setSearchTimeout(timeout);

    return () => {
      clearTimeout(timeout);
    };
  }, [currentPage, searchValue, filterDate]);

  // Manipuladores de eventos
  const handlePageChange = (_: unknown, newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo(0, 0);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(verifyNumberOrString(value) ? maskCPF(value) : value);
    setCurrentPage(1);
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterDate(e.target.value);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchValue('');
    setFilterDate('');
    setCurrentPage(1);
  };

  const closeDialog = () => {
    setDialogDelete(false);
    setEdit(null);
  };

  const deleteAgendamento = (agenda: IAgenda) => {
    setEdit(agenda);
    setDialogDelete(true);
  };

  const confirmDelete = async () => {
    if (!edit) return;
    try {
      const res = await api.delete(`/ravim/exclu-agendamento`, {
        data: { id_agendamento: String(edit.id_agendamento) }
      });
      setSnack(true);
      setMessage(res.data.message || 'Agendamento excluído com sucesso!');
      setSeverity('success');
      closeDialog();
      fetchData(currentPage, searchValue, filterDate);
    } catch (err: any) {
      setSnack(true);
      setMessage(err.response?.data?.message || 'Erro ao excluir agendamento.');
      setSeverity('error');
    }
  };

  const handleSnackClose = () => setSnack(false);

  const formatDate = (dateString: string) => dateString.replace(/(\d*)-(\d*)-(\d*).*/, '$3/$2/$1');

  // Renderização (JSX)
  return (
    <Container>
      <ContainerButtonsTop>
        <DivSearch>
          <TextField
            label="Pesquisar por Nome ou CPF"
            type="text"
            variant="outlined"
            value={searchValue}
            inputProps={{ maxLength: verifyNumberOrString(searchValue) ? 14 : undefined }}
            onChange={handleSearchChange}
            fullWidth
          />
          <TextField
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            type="date"
            label="Buscar Por Data"
            value={filterDate}
            onChange={handleDateChange}
          />
          {(searchValue || filterDate) && (
            <Button
              variantstyle="RED"
              icon={<X size={20} />}
              onClick={handleClearSearch}
              title="Limpar filtros"
            />
          )}
        </DivSearch>
      </ContainerButtonsTop>

      <Typography variant="h4" align="center" style={{ margin: '20px 0', fontWeight: 'bold' }}>
        Agendamentos Externo
      </Typography>

      <Typography variant="h6" align="left" style={{ marginBottom: 16 }}>
        Total de registros: {totalRecords}
      </Typography>

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" marginBottom={2}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}

      <TableContainer component={Paper} elevation={3}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell><strong>Colaborador</strong></TableCell>
              <TableCell><strong>CPF</strong></TableCell>
              <TableCell><strong>Nome Comercial</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Data</strong></TableCell>
              <TableCell><strong>Horário</strong></TableCell>
              <TableCell><strong>Tipo</strong></TableCell>
              <TableCell><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" style={{ padding: '40px 0' }}>
                  <CircularProgress size={40} />
                  <Typography variant="body1" style={{ marginTop: 16 }}>
                    Carregando Agendamentos...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : data.length > 0 ? (
              data.map((item) => (
                <TableRow key={item.id_agendamento} hover>
                  <TableCell>{item.nome}</TableCell>
                  <TableCell>{maskCPF(item.cpf_colaborador)}</TableCell>
                  <TableCell>{item.nome_comercial}</TableCell>
                  <TableCell>{item.status}</TableCell>
                  <TableCell>{formatDate(item.data_agendamento)}</TableCell>
                  <TableCell>{item.horario}</TableCell>
                  <TableCell>{item.tipo_agendamento}</TableCell>
                  <TableCell>
                    <Box display="flex">
                      <Link to={{ pathname: `/editar-agendamento/${item.id_agendamento}`, state: item }}>
                        <IconButton title="Editar" aria-label="editar">
                          <SettingsBackupRestore color="primary" />
                        </IconButton>
                      </Link>
                      <IconButton title="Excluir" aria-label="deletar" onClick={() => deleteAgendamento(item)}>
                        <HighlightOff color="error" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center" style={{ padding: '40px 0' }}>
                  <Typography variant="h6">
                    Nenhum agendamento encontrado com os filtros aplicados.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" marginTop={3}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}

      <Dialog open={dialogDelete} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>Você está prestes a excluir o agendamento de:</Typography>
          <Typography><strong>Nome:</strong> {edit?.nome || '-'}</Typography>
          <Typography><strong>Data:</strong> {edit?.data_agendamento ? formatDate(edit.data_agendamento) : '-'}</Typography>
          <Typography color="error" style={{ marginTop: 16, fontWeight: 'bold' }}>
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} title="Cancelar" variantstyle="SECUNDARY" />
          <Button onClick={confirmDelete} title="Confirmar Exclusão" variantstyle="RED" autoFocus />
        </DialogActions>
      </Dialog>

      <Snackbar open={snack} autoHideDuration={6000} onClose={handleSnackClose} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleSnackClose} severity={severity}>
          {message}
        </Alert>
      </Snackbar>
    </Container>
  );
};
